/** @jsxImportSource @emotion/react */
import { Table } from "../../../components/table-components/Table";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { PageFooter } from "../../../components/page-skeleton/PageFooter";
import { StyledText } from "../../../assets/typography/typography";
import { KitchenCategory } from "../models/kitchen-category";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { css } from "@emotion/react";
import { colors } from "../../../components/colors";
import { MutateKitchenCategoryDrawer } from "./MutateKitchenCategoryDrawer";
import { Fragment, MutableRefObject, useState } from "react";
import Sortable from "sortablejs";
import { DragAndDropContainer } from "../../../components/DragAndDropContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useUpdateKitchenCategorySorting } from "../service/kitchen-categories-api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { SpinnerBlock } from "../../common/Spinner";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";

type Props = {
  kitchenCategories: KitchenCategory[];
};

const sortTableCellStyle = css`
  cursor: grab;
`;

const dragAndDropContainerStyle = css`
  border-bottom: 1px solid ${colors.greyBorder};
`;

export const KitchenCategoriesTable = ({ kitchenCategories }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { setOption, option, removeOption } = useSearchParamsManipulation();
  const [categoryToEditId, setCategoryToEditId] = useState<string>(
    option || ""
  );

  const {
    mutateAsync: updateSorting,
    isLoading: isUpdatingKitchenCategoriesSorting,
  } = useUpdateKitchenCategorySorting();

  const onDrop = async (
    event: Sortable.SortableEvent,
    containerRef: MutableRefObject<HTMLElement | null>
  ) => {
    if (!containerRef) {
      return;
    }
    // iterating over dom children. Each of them has the id of the category as data attribute
    // @ts-expect-error
    const categories = [...containerRef.current?.children].map(
      (b) => b.dataset.id
    );
    try {
      await updateSorting({
        data: {
          sort: categories,
        },
        organizationKeyName: organizationKeyName,
      });
      toastr.success("Success", "Kitchen categories reordered successfully");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  if (isUpdatingKitchenCategoriesSorting) {
    return <SpinnerBlock />;
  }

  return (
    <div>
      <Table>
        <thead>
          <TableRowHeader>
            <TableCellHeaderText>Sorting</TableCellHeaderText>
            <TableCellHeaderText>Name</TableCellHeaderText>
            <TableCellHeaderText>Title</TableCellHeaderText>
            <TableCellHeaderText>Description</TableCellHeaderText>
            <th />
          </TableRowHeader>
        </thead>
        <DragAndDropContainer
          isTable={true}
          onEnd={onDrop}
          containerStyle={dragAndDropContainerStyle}
        >
          {kitchenCategories
            ?.sort((a, b) => a.sortKey - b.sortKey)
            .map((cat, i) => {
              return (
                <Fragment key={i}>
                  <TableRow data-id={cat.id}>
                    <TableCellText className="pl-5" css={sortTableCellStyle}>
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        <FontAwesomeIcon icon={solid("grip-vertical")} />
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(cat.id);
                        setCategoryToEditId(cat.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {cat.name}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(cat.id);
                        setCategoryToEditId(cat.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {cat.title}
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(cat.id);
                        setCategoryToEditId(cat.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {cat.description}
                      </StyledText>
                    </TableCellText>
                    <TableEllipsisDropdown
                      menu={{
                        items: [
                          {
                            key: "1",
                            label: (
                              <DropdownItemButton
                                onClick={() => {
                                  setOption(cat.id);
                                  setCategoryToEditId(cat.id);
                                }}
                                type="button"
                                icon={<FontAwesomeIcon icon={solid("pen")} />}
                              >
                                Edit kitchen category
                              </DropdownItemButton>
                            ),
                          },
                        ],
                      }}
                    />
                  </TableRow>
                </Fragment>
              );
            })}
        </DragAndDropContainer>
      </Table>
      <MutateKitchenCategoryDrawer
        isOpen={!!categoryToEditId}
        onClose={() => {
          removeOption();
          setCategoryToEditId("");
        }}
        id={categoryToEditId}
      />
      <PageFooter>
        <StyledText className="bodyTextRegular mb-0">
          {kitchenCategories?.length}{" "}
          {kitchenCategories?.length === 1
            ? "kitchen category"
            : "kitchen categories"}{" "}
          in total
        </StyledText>
      </PageFooter>
    </div>
  );
};
